import React, { useContext } from 'react';
import {
  ButtonDropdown,
  ButtonDropdownProps,
} from '@cloudscape-design/components';
import { AccountContext } from 'src/context/accountContext';

export type PermissionLevel =
  | 'READ_ONLY'
  | 'READ_AND_LIMITED_ACTION'
  | 'ADMINISTER';

export interface PermissionAwareButtonDropdownItemProps
  extends ButtonDropdownProps.Item {
  requiredPermission: PermissionLevel;
  actionDescription: string;
}

export interface PermissionAwareButtonButtonDropdownProps
  extends ButtonDropdownProps {
  items: PermissionAwareButtonDropdownItemProps[];
}

// Map permission levels to numeric values for easier comparison
// Higher number = more permissions
const permissionLevelToValue: Record<PermissionLevel, number> = {
  READ_ONLY: 1,
  READ_AND_LIMITED_ACTION: 2,
  ADMINISTER: 3,
};

// Map permission levels to user-friendly descriptions
const permissionLevelToFriendlyName: Record<PermissionLevel, string> = {
  READ_ONLY: 'read-only',
  READ_AND_LIMITED_ACTION: 'limited action',
  ADMINISTER: 'administrator',
};

export const PermissionAwareButtonDropdown: React.FC<
  PermissionAwareButtonButtonDropdownProps
> = ({ ...buttonProps }) => {
  const { selectedAccount, loading } = useContext(AccountContext);
  const userPermission = selectedAccount?.permissionLevel || 'ADMINISTER';

  // Check if user has required permission using numeric comparison
  const hasPermission = (item: PermissionAwareButtonDropdownItemProps) => {
    const userPermissionValue =
      permissionLevelToValue[userPermission] ||
      permissionLevelToValue.ADMINISTER;
    const requiredPermissionValue =
      permissionLevelToValue[item.requiredPermission];

    return userPermissionValue >= requiredPermissionValue;
  };

  // Generate appropriate disabled reason message
  const getDisabledReason = (item: PermissionAwareButtonDropdownItemProps) => {
    if (!hasPermission(item)) {
      const friendlyName =
        permissionLevelToFriendlyName[item.requiredPermission] || 'appropriate';
      return `You need ${friendlyName} permissions to ${item.actionDescription}`;
    }
    return undefined;
  };

  // look through each item and see if it should be disabled
  const updatedItems = buttonProps.items.map((item) => {
    return {
      ...item,
      requiredPermission: undefined,
      actionDescription: undefined,
      disabled: item.disabled ?? !hasPermission(item),
      disabledReason: item.disabledReason ?? getDisabledReason(item),
    };
  });

  return (
    <ButtonDropdown loading={loading} {...buttonProps} items={updatedItems}>
      {buttonProps.children}
    </ButtonDropdown>
  );
};
